import { useEffect, useState } from "react";

import { track } from "common/utils/mixpanel/tracker";

import "./TechReportPicker.scss";

declare global {
  interface Window {
    showTroubleshootingSteps?: () => void;
  }
}

export interface TechReportPickerProps {
  defectiveHref: string;
  lateHref: string;
  storeCode: string;
}

type PickerState = "collapsed" | "expanded";

export function isSafePath(href: string) {
  return href.startsWith("/") && !href.startsWith("//");
}

function makeRedirectClickHandler(destination: "defect" | "late", href: string, storeCode: string) {
  const option = destination === "defect" ? "defective" : "late";
  return (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isSafePath(href)) {
      event.preventDefault();
      return;
    }
    // Both events fire sendBeacon so they survive the navigation that's about
    // to happen. AC requires firing BEFORE the page unloads.
    track(
      "tech_report_option_picked",
      { option, store_code: storeCode },
      { transport: "sendBeacon", send_immediately: true }
    );
    track(
      "tech_report_redirected",
      { destination, store_code: storeCode },
      { transport: "sendBeacon", send_immediately: true }
    );
    // No preventDefault — let the browser perform native navigation.
  };
}

export function TechReportPicker({ defectiveHref, lateHref, storeCode }: TechReportPickerProps) {
  const [state, setState] = useState<PickerState>("collapsed");

  // Mount effect must run before any early return so hook order is stable
  // across renders (React Rules of Hooks). React's commit phase only runs
  // after a successful render, so render-throws leave the form visible —
  // satisfying the no-JS / boot-failure fallback requirement.
  useEffect(() => {
    document.getElementById("technical-form-wrapper")?.setAttribute("hidden", "");
  }, []);

  if (state === "expanded") return null;

  const handleSiteIssue = () => {
    // No sendBeacon — this path doesn't navigate, so the regular async XHR is
    // fine and avoids the sendBeacon Content-Type rewrite. No redirected event
    // pairs with this one; the user stays on the page to read the troubleshooting
    // steps before deciding to expand the form (No) or leave (Yes).
    track("tech_report_option_picked", { option: "site_issue", store_code: storeCode });
    // The `?.` is a defensive hydration-race guard: the global is defined in a
    // server-rendered <script> on the same page. If the user clicks before that
    // script executes, this avoids a TypeError. Stryker can't differentiate this
    // from a redundant guard because unit tests can't simulate pre-hydration timing.
    // Stryker disable next-line OptionalChaining
    window.showTroubleshootingSteps?.();
    setState("expanded");
  };

  // Sanitize hrefs at render time so middle-click / right-click → "open in new tab"
  // also no-ops on unsafe paths (onClick alone doesn't fire for those gestures).
  const safeDefectiveHref = isSafePath(defectiveHref) ? defectiveHref : "#";
  const safeLateHref = isSafePath(lateHref) ? lateHref : "#";

  return (
    <section aria-labelledby="tech-report-picker-heading">
      <p
        className="tech-report-picker__prompt"
        id="tech-report-picker-heading"
        role="heading"
        aria-level={2}
      >
        What can we help you with?
      </p>
      <div className="tech-report-picker tech-report-picker--collapsed">
        <div className="tech-report-picker__actions">
          <a
            href={safeDefectiveHref}
            className="tech-report-picker__button"
            data-testid="tech-report-picker-defective"
            onClick={makeRedirectClickHandler("defect", defectiveHref, storeCode)}
          >
            I received a defective order
          </a>
          <a
            href={safeLateHref}
            className="tech-report-picker__button"
            data-testid="tech-report-picker-late"
            onClick={makeRedirectClickHandler("late", lateHref, storeCode)}
          >
            I haven&rsquo;t received the order
          </a>
          <button
            type="button"
            className="tech-report-picker__button"
            data-testid="tech-report-picker-site-issue"
            onClick={handleSiteIssue}
          >
            I am facing an issue with the Jiffy site
          </button>
        </div>
      </div>
    </section>
  );
}
